import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { BARRIERS_URL } from '../../api-urls'
import { DataProviderExtensionResult } from '../../common/data-provider'
import { DateTime } from '../../common/date-time'
import {
  auditCommentParamComposer,
  get,
  getWithPagination,
  patch,
  post,
  put,
  recurringJobParamsComposer,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { RecurringJobCreationParamsDto } from '../../dto/recurring-job-creation-params.dto'
import { RecurringJobDetailsDto } from '../../dto/recurring-job-details.dto'
import { getDeviceCategoryChildren } from '../devices.utils'
import filterMapper from './barrier-filter.mapper'
import mapSortPassageParam from './barrier-sort.mapper'
import { AuditCommentRequestDto } from '../../dto/audit-comment-request.dto'
import { AuditCommentParamsDto } from '../../dto/audit-comment-params.dto'
import { BarrierDto } from '../../dto/device/barrier/barrier.dto'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<BarrierDto>> => {
    let extendedFilter = filter
    // Tree type filter for device categories
    if (extendedFilter?.categoryIdWithDescendants) {
      const categoriesIds = await getDeviceCategoryChildren(
        extendedFilter.categoryIdWithDescendants,
      )
      extendedFilter = { ...extendedFilter, categoriesIds }
    }
    const filterParams = `o=>${filterParamsComposer(
      'o',
      extendedFilter,
      filterMapper,
    )}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPassageParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<BarrierDto[]>(
      `${BARRIERS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<BarrierDto>> => {
    const data = await get<BarrierDto>(BARRIERS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<BarrierDto>> => {
    const { data } = await getWithPagination<BarrierDto[]>(
      `${BARRIERS_URL}/GetByConditions/`,
      `e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreatePassageRequest>,
  ): Promise<CreateResult<BarrierDto>> => {
    const passage = await post<CreatePassageRequest[], BarrierDto[]>(
      BARRIERS_URL,
      [data],
    )
    return {
      data: passage[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<BarrierDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      BarrierDto
    >(BARRIERS_URL, {
      id,
      name: data.name !== previousData.name ? data.name : undefined,
      isWatched:
        data.isWatched !== previousData.isWatched ? data.isWatched : undefined,
      categoryId:
        data.categoryId !== previousData.categoryId
          ? data.categoryId
          : undefined,
      cameraId:
        data.cameraId !== previousData.cameraId ? data.cameraId : undefined,
    })
    return Promise.resolve({ data: update })
  },
  setPowerState: async (
    resource: string,
    { barrierId, powerState, ...rest }: SetPowerStateRequest,
  ): Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  > => {
    const recurringJobQuery =
      rest && recurringJobParamsComposer(rest as RecurringJobCreationParamsDto)
    const auditCommentQuery =
      rest && auditCommentParamComposer(rest as AuditCommentParamsDto)
    const queryParams: string[] = []
    if (recurringJobQuery) {
      queryParams.push(recurringJobQuery)
    }
    if (auditCommentQuery) {
      queryParams.push(auditCommentQuery)
    }
    const query = queryParams?.length
      ? queryParams.reduce((p, c) => `${p}&${c}`)
      : undefined

    const setPowerState = await put<
      SetPowerStateRequest,
      ChangeStateResponse | RecurringJobDetailsDto
    >(`${BARRIERS_URL}/SetPowerState/${query ? `?${query}` : ''}`, {
      barrierId,
      powerState,
    })

    if ((setPowerState as RecurringJobDetailsDto)?.recurringJobId)
      return {
        data: setPowerState as RecurringJobDetailsDto,
      }
    return {
      data: setPowerState,
    }
  },
  setPPOZState: async (
    resource: string,
    { barrierId, ppozState, ...rest }: SetPPOZStateRequest,
  ): Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  > => {
    const recurringJobQuery =
      rest && recurringJobParamsComposer(rest as RecurringJobCreationParamsDto)
    const auditCommentQuery =
      rest && auditCommentParamComposer(rest as AuditCommentParamsDto)
    const queryParams: string[] = []
    if (recurringJobQuery) {
      queryParams.push(recurringJobQuery)
    }
    if (auditCommentQuery) {
      queryParams.push(auditCommentQuery)
    }
    const query = queryParams?.length
      ? queryParams.reduce((p, c) => `${p}&${c}`)
      : undefined

    const setPPOZState = await put<
      SetPPOZStateRequest,
      ChangeStateResponse | RecurringJobDetailsDto
    >(`${BARRIERS_URL}/SetPPOZState/${query ? `?${query}` : ''}`, {
      barrierId,
      ppozState,
    })

    if ((setPPOZState as RecurringJobDetailsDto)?.recurringJobId)
      return {
        data: setPPOZState as RecurringJobDetailsDto,
      }
    return {
      data: setPPOZState,
    }
  },
  setFreePassState: async (
    resource: string,
    { barrierId, freePassState, ...rest }: SetFreePassStateRequest,
  ): Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  > => {
    const recurringJobQuery =
      rest && recurringJobParamsComposer(rest as RecurringJobCreationParamsDto)
    const auditCommentQuery =
      rest && auditCommentParamComposer(rest as AuditCommentParamsDto)
    const queryParams: string[] = []
    if (recurringJobQuery) {
      queryParams.push(recurringJobQuery)
    }
    if (auditCommentQuery) {
      queryParams.push(auditCommentQuery)
    }
    const query = queryParams?.length
      ? queryParams.reduce((p, c) => `${p}&${c}`)
      : undefined

    const setFreePassState = await put<
      SetFreePassStateRequest,
      ChangeStateResponse | RecurringJobDetailsDto
    >(`${BARRIERS_URL}/SetFreePassState/${query ? `?${query}` : ''}`, {
      barrierId,
      freePassState,
    })

    if ((setFreePassState as RecurringJobDetailsDto)?.recurringJobId)
      return {
        data: setFreePassState as RecurringJobDetailsDto,
      }
    return {
      data: setFreePassState,
    }
  },
  forceReportState: async (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ): Promise<DataProviderExtensionResult<undefined>> => {
    const forceReportState = await get<undefined>(
      BARRIERS_URL,
      `/ForceReportState/${params?.id}`,
    )
    return {
      data: forceReportState,
    }
  },
  resetModules: async (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ): Promise<DataProviderExtensionResult<undefined>> => {
    const forceReportState = await get<undefined>(
      BARRIERS_URL,
      `/ResetModules/${params?.id}`,
    )
    return {
      data: forceReportState,
    }
  },
} as DetectorDataProvider

interface DetectorDataProvider extends DataProvider {
  setPowerState: (
    resource: string,
    params: SetPowerStateRequest,
  ) => Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  >

  setPPOZState: (
    resource: string,
    params: SetPPOZStateRequest,
  ) => Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  >

  setFreePassState: (
    resource: string,
    params: SetFreePassStateRequest,
  ) => Promise<
    DataProviderExtensionResult<ChangeStateResponse | RecurringJobDetailsDto>
  >

  forceReportState: (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ) => Promise<DataProviderExtensionResult<undefined>>

  resetModules: (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ) => Promise<DataProviderExtensionResult<undefined>>
}

export interface CreatePassageRequest {
  readonly name: string
  readonly isWatched?: boolean
  readonly lastRefresh?: DateTime
  readonly categoryId?: number
  readonly jsonStatus?: string
  readonly tsCanId: number
}

export interface SetFreePassStateRequest
  extends RecurringJobCreationParamsDto,
    AuditCommentRequestDto {
  readonly barrierId: number
  readonly freePassState: boolean
}

export interface SetPowerStateRequest
  extends RecurringJobCreationParamsDto,
    AuditCommentRequestDto {
  readonly barrierId: number
  readonly powerState: boolean
}

export interface SetPPOZStateRequest
  extends RecurringJobCreationParamsDto,
    AuditCommentRequestDto {
  readonly barrierId: number
  readonly ppozState: boolean
}

interface ChangeStateResponse {
  jobId: Identifier
}

export default provider
